import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ICVData, ILayout } from 'types/layoutInterfaces'
import About from './About'
import Education from './Education'
import LanguageSkills from './LanguageSkills'
import Projects from './Projects'
import References from './References'
import Skills from './Skills'
import Roles from './Skills'
import Industries from './Skills'
import LeisureActivities from './LeisureActivities'
import Url from './Url'
import WorkHistory from './WorkHistory'
import { usePreviewData } from '../PrintPreviewProvider'
import { layoutAPI } from 'api/LayoutAPI'

/** @notExported */
interface IControlSectionsProps {
  /** CV data  */
  data: ICVData
  /** CV layout */
  layout: ILayout
  /** Function to hide section */
  hideSection: (value: boolean, type: string) => void
  /** Function to hide item */
  hideItem: (value: boolean, id: number, type: string) => void
}

/**
 * Control sections component.
 *
 * @returns Control sections component.
 * @notExported
 */
const ControlSections: React.FC<IControlSectionsProps> = ({ data, layout, hideSection, hideItem }) => {
  const { t } = useTranslation()
  const { visibleLayout, setVisibleLayout } = usePreviewData()
  const [aboutStatus, setAboutStatus] = useState<boolean>(false)
  const [educationStatus, setEducationStatus] = useState<boolean>(false)
  const [workHistoryStatus, setWorkHistoryStatus] = useState<boolean>(false)
  const [projectsStatus, setProjectsStatus] = useState<boolean>(false)
  const [referencesStatus, setReferencesStatus] = useState<boolean>(false)
  const [skillsStatus, setSkillsStatus] = useState<boolean>(false)
  const [industriesStatus, setIndustriesStatus] = useState<boolean>(false)
  const [rolesStatus, setRolesStatus] = useState<boolean>(false)
  const [languageSkillsStatus, setLanguageSkillsStatus] = useState<boolean>(false)
  const [urlStatus, setUrlStatus] = useState<boolean>(false)
  const [leisureActivitiesStatus, setLeisureActivitiesStatus] = useState<boolean>(false)
  const educations = {
    degrees: data.degrees,
    courses: data.courses,
    certificates: data.personCertificates,
    competences: data.personCompetences,
  }

  const setStatus = (value: boolean, sectionType: string) => {
    if (value === true) {
      if (sectionType === 'about') setAboutStatus(false)
      if (sectionType === 'education') setEducationStatus(false)
      if (sectionType === 'employments') setWorkHistoryStatus(false)
      if (sectionType === 'projects') setProjectsStatus(false)
      if (sectionType === 'references') setReferencesStatus(false)
      if (sectionType === 'skills') setSkillsStatus(false)
      if (sectionType === 'industries') setIndustriesStatus(false)
      if (sectionType === 'roles') setRolesStatus(false)
      if (sectionType === 'languages') setLanguageSkillsStatus(false)
      if (sectionType === 'url') setUrlStatus(false)
      if (sectionType === 'leisureActivities') setLeisureActivitiesStatus(false)
    }
  }

  const closeAll = () => {
    setAboutStatus(false)
    setEducationStatus(false)
    setWorkHistoryStatus(false)
    setProjectsStatus(false)
    setReferencesStatus(false)
    setSkillsStatus(false)
    setIndustriesStatus(false)
    setRolesStatus(false)
    setLanguageSkillsStatus(false)
    setUrlStatus(false)
    setLeisureActivitiesStatus(false)
  }

  const hideAndCloseSection = (value: boolean, type: string) => {
    hideSection(value, type)
    setStatus(value, type)
  }

  const handleHideSettings = async (data: Record<string, boolean>, type: string) => {
    if (!visibleLayout) return
    const layout = await layoutAPI.changeHideSetting(visibleLayout?.id, type, data)
    setVisibleLayout(layout)
  }

  return (
    <Grid container spacing={2}>
      {(aboutStatus ||
        educationStatus ||
        workHistoryStatus ||
        projectsStatus ||
        referencesStatus ||
        skillsStatus ||
        industriesStatus ||
        rolesStatus ||
        languageSkillsStatus ||
        urlStatus ||
        leisureActivitiesStatus) && (
        <Button
          data-testid="button-print-control-sections-close-all"
          size="small"
          onClick={closeAll}
          style={{ margin: '4px' }}
        >
          {t('print.layout.closeAll')}
        </Button>
      )}
      <Grid size={12}>
        <About
          about={data.about}
          layout={layout}
          hideSection={hideAndCloseSection}
          expanded={aboutStatus}
          setStatus={setAboutStatus}
        />
      </Grid>
      <Grid size={12}>
        <Skills
          type="skills"
          items={data.skills}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={skillsStatus}
          setStatus={setSkillsStatus}
        />
      </Grid>
      <Grid size={12}>
        <Industries
          type="industries"
          items={data.industries}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={industriesStatus}
          setStatus={setIndustriesStatus}
        />
      </Grid>
      <Grid size={12}>
        <Roles
          type="roles"
          items={data.roles}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={rolesStatus}
          setStatus={setRolesStatus}
        />
      </Grid>
      <Grid size={12}>
        <WorkHistory
          employments={data.employments}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={workHistoryStatus}
          setStatus={setWorkHistoryStatus}
          handleHideSettings={handleHideSettings}
        />
      </Grid>
      <Grid size={12}>
        <Projects
          projects={data.projects}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={projectsStatus}
          setStatus={setProjectsStatus}
          handleHideSettings={handleHideSettings}
        />
      </Grid>
      <Grid size={12}>
        <Education
          educations={educations}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={educationStatus}
          setStatus={setEducationStatus}
          handleHideSettings={handleHideSettings}
        />
      </Grid>
      <Grid size={12}>
        <References
          references={data.references}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={referencesStatus}
          setStatus={setReferencesStatus}
          handleHideSettings={handleHideSettings}
        />
      </Grid>
      <Grid size={12}>
        <LanguageSkills
          languages={data.languages}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={languageSkillsStatus}
          setStatus={setLanguageSkillsStatus}
        />
      </Grid>
      <Grid size={12}>
        <LeisureActivities
          leisureActivities={data.leisureActivities ?? []}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={leisureActivitiesStatus}
          setStatus={setLeisureActivitiesStatus}
          handleHideSettings={handleHideSettings}
        />
      </Grid>
      <Grid size={12}>
        <Url
          urls={data.urls}
          layout={layout}
          hideSection={hideAndCloseSection}
          hideItem={hideItem}
          expanded={urlStatus}
          setStatus={setUrlStatus}
        />
      </Grid>
    </Grid>
  )
}

export default ControlSections
